import * as React from 'react';
import isFunction from 'lodash/isFunction';
import { connect } from 'react-redux';

import MediaPreview from 'components/MediaPreview';
import GalleryModalAsset from 'components/gallery-modal/gallery-modal-asset/GalleryModalAsset';
import { withModal } from 'components/modal/ModalHOC';
import LikeContentActionButton from 'components/like-content-action-button/LikeContentActionButton';

import ContentGalleryContext from '../../context/ContentGalleryContext';
import ContentGalleryAssetOverlay from './overlay/ContentGalleryAssetOverlay';
import ContentGalleryThumbnailOverlay from '../thumbnail/overlay/ContentGalleryThumbnailOverlay';

import { markAsFavourite } from 'modules/shared-access/reducers/clientApprovalV2';

import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { getItemSize } from '../../utils/content-gallery';

import type {
  GalleryModalItem
} from 'types';
import type {
  ContentGalleryItem
} from '../../types';

type OwnProps = {
  item: ContentGalleryItem,
  galleryItems: Array<GalleryModalItem>,
  galleryItemIndex: number
};

type DispatchProps = {
  dispatchMarkAsFavourite: (markAsFav: boolean, bucketId: number, assetId: number) => void
}

type StateProps = {
  markAsFavLoadingAsset: {
    [itemId: number]: boolean
  }
}

type Props = OwnProps & DispatchProps & StateProps;

const DivWithGalleryModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);

const ContentGalleryAsset: React.FunctionComponent<Props> = ({
  item,
  galleryItems,
  galleryItemIndex,
  markAsFavLoadingAsset,
  dispatchMarkAsFavourite
}: Props) => {
  const context = React.useContext(ContentGalleryContext);
  const renderGalleryAssetOverlay = context?.renderGalleryAssetOverlay;

  const renderThumbnailOverlay = (
    selectedItem: GalleryModalItem
  ): React.ReactNode => (
    <ContentGalleryContext.Consumer key={item.id}>
      {(localContext) => (
        <ContentGalleryThumbnailOverlay
          selectedItem={selectedItem}
          items={localContext?.items}
        />
      )}
    </ContentGalleryContext.Consumer>
  );

  const handleMarkAsFav = (event: React.SyntheticEvent<HTMLElement>) => {
    event.stopPropagation();

    if (item.bucketId) {
      dispatchMarkAsFavourite(!item.favourite, item.bucketId, Number(item.id));
    }
  };

  const renderAsset = (
    selectedItem: GalleryModalItem,
    currentPosition: number
  ): React.ReactNode => {
    if (!isFunction(renderGalleryAssetOverlay)) {
      return (
        <GalleryModalAsset
          selectedItem={selectedItem}
          currentPosition={currentPosition}
        />
      );
    }

    return (
      <ContentGalleryContext.Consumer key={item.id}>
        {(localContext) => (
          <ContentGalleryAssetOverlay
            contents={renderGalleryAssetOverlay(
              selectedItem,
              localContext?.items,
              dispatchMarkAsFavourite
            )}
            selectedItem={selectedItem}
            currentPosition={currentPosition}
          />
        )}
      </ContentGalleryContext.Consumer>
    );
  };

  const itemSize = getItemSize(context?.itemSize);

  return (
    <div className="content-gallery-asset">
      <DivWithGalleryModal
        modalContentProps={{
          items: galleryItems,
          currentPosition: galleryItemIndex,
          renderCustomAsset: renderAsset,
          renderCustomThumbnailOverlay: renderThumbnailOverlay
        }}
      >
        {
          item.status === 'approved' && (
            <LikeContentActionButton
              isLoading={markAsFavLoadingAsset[item.id]}
              onClick={handleMarkAsFav}
              isFavourite={item.favourite}
            />
          )
        }
        <MediaPreview
          key={`content-gallery-asset--${item.id}`}
          attachment={{
            ...item,
            format: item.mediaType || item.assetType
          }}
          width={itemSize}
          height={itemSize}
        />
      </DivWithGalleryModal>
    </div>
  );
};

const mapStateToProps = ({ clientApprovalV2 }): StateProps => ({
  markAsFavLoadingAsset: clientApprovalV2.markAsFavLoadingAsset
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchMarkAsFavourite: (markAsFav: boolean, bucketId: number, itemId: number) => (
    dispatch(markAsFavourite(markAsFav, bucketId, itemId))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(ContentGalleryAsset);
